import { Attr, Field, Model, Names } from '@mode-switch/model';
import { PivotBasic, PivotQuery } from '@mode-switch/query';
import { EncField, Encoding, VariableType } from '@mode-vegas/vegas';
import { partition } from 'lodash';

import { DataRole } from '@mode-switch/express';
import { getGrandTotalCandidacy, getSubtotalsCandidacy } from '../../total-helpers';
import { ModeRole, PlanSummary } from './pivot-query.plan';

type FieldMap = Map<string, Field<ModeRole>>;

/**
 * @description GrandTotal Query builder.
 */
export function buildGrandTotalQueries(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  summary: PlanSummary<ModeRole>
): PivotBasic<ModeRole>[] {
  const plans: PivotBasic<ModeRole>[] = [];
  const totals = getGrandTotalCandidacy(encoding, model, viewName);
  const hasColTotal = encoding.totals?.grand.column === true && totals.col;
  const hasRowTotal = encoding.totals?.grand.row === true && totals.row;

  // Bail early if we can't produce any GrandTotals or
  // they are not enabled in the encoding
  if (!(hasColTotal || hasRowTotal)) {
    return plans;
  }

  for (const { query, series } of summary.queries) {
    if (hasRowTotal) {
      const [[column, x], removed] = dropDiscreteOfChannels([query.column, query.x]);
      const marks = removeMarksMatching(query.marks, removed);
      addBasic('row', series, {
        ...query,
        column,
        x,
        marks,
      });
    }

    if (hasColTotal) {
      const [[row, y], removed] = dropDiscreteOfChannels([query.row, query.y]);
      const marks = removeMarksMatching(query.marks, removed);
      addBasic('col', series, {
        ...query,
        row,
        y,
        marks,
      });
    }

    if (hasRowTotal && hasColTotal && totals.all) {
      const [[column, x, row, y], removed] = dropDiscreteOfChannels([query.column, query.x, query.row, query.y]);
      const marks = removeMarksMatching(query.marks, removed);
      addBasic('all', series, {
        ...query,
        column,
        x,
        row,
        y,
        marks,
      });
    }
  }

  return plans;

  /*
   * Helpers with access to some local declarations,
   * cleans up the body of the function a bit
   */

  function addBasic(key: string, series: string, query: Partial<PivotQuery.Basic<ModeRole>>) {
    query = dropUnusedSorts(query);

    const name = `gt:${key}:${series}`;
    const basicPlan = PivotBasic.make(
      model,
      viewName,
      PivotQuery.Basic.make({
        ...query,
        name,
      })
    );

    // Shouldn't happen
    if (basicPlan.queries.length !== 1) {
      throw new Error(`Grand Totals addBasic(): Expected 1 query, saw ${basicPlan.queries.length}`);
    }

    plans.push(basicPlan);
  }
}

/**
 * @description Subtotal Query builder.
 */
export function buildSubtotalQueries(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  summary: PlanSummary<ModeRole>
): PivotBasic<ModeRole>[] {
  const plans: PivotBasic<ModeRole>[] = [];

  const subtotalCandidacy = getSubtotalsCandidacy(encoding, model, viewName);
  const hasColSubtotal = encoding.totals?.subtotal.column === true && subtotalCandidacy.col;
  const hasRowSubtotal = encoding.totals?.subtotal.row === true && subtotalCandidacy.row;

  // Bail early if we can't produce any Subtotals
  if (!(hasColSubtotal || hasRowSubtotal)) {
    return plans;
  }

  const grandTotalCandidacy = getGrandTotalCandidacy(encoding, model, viewName);
  const hasColGrandTotal = encoding.totals?.grand.column === true && grandTotalCandidacy.col;
  const hasRowGrandTotal = encoding.totals?.grand.row === true && grandTotalCandidacy.row;

  for (const { query, series } of summary.queries) {
    if (hasRowSubtotal) {
      buildRowSubtotals(model, viewName, plans, query, series, hasColGrandTotal);
    }
    if (hasColSubtotal) {
      buildColumnSubtotals(model, viewName, plans, query, series, hasRowGrandTotal);
    }
    if (hasRowSubtotal && hasColSubtotal) {
      buildCornerSubtotals(model, viewName, plans, query, series);
    }
  }

  return plans;
}

function buildRowSubtotals(
  model: Model<ModeRole>,
  viewName: string,
  plans: PivotBasic<ModeRole>[],
  query: PivotQuery.Basic<DataRole>,
  series: string,
  hasColGrandTotal: boolean
) {
  buildSubtotals(model, viewName, plans, query, ['column', 'x'], 'row', series, hasColGrandTotal);
}

function buildColumnSubtotals(
  model: Model<ModeRole>,
  viewName: string,
  plans: PivotBasic<ModeRole>[],
  query: PivotQuery.Basic<DataRole>,
  series: string,
  hasRowGrandTotal: boolean
) {
  buildSubtotals(model, viewName, plans, query, ['row', 'y'], 'col', series, hasRowGrandTotal);
}

function buildCornerSubtotals(
  model: Model<ModeRole>,
  viewName: string,
  plans: PivotBasic<ModeRole>[],
  query: PivotQuery.Basic<DataRole>,
  series: string
) {
  /**
   * Permutations of Row and Column Subtotals [for the corners]..
   * ---
   *
   * ## Given a configuration:
   *
   * COLUMNS -> @D(Q of Order Created Date) @D(Region Name) @D(Web Event Channel)
   * ROWS    -> @D(Sales Rep Name) @D(Web Event Quarter) @D(Account Name)
   *
   * ## We should create 4 subtotal queries for the corners:
   *
   * Pass 1 (
   *   COLUMNS -> @D(Q of Order Created Date) @D(Region Name)
   *   ROWS    -> @D(Sales Rep Name) @D(Web Event Quarter)
   * )
   *
   * Pass 2 (
   *   COLUMNS -> @D(Q of Order Created Date) @D(Region Name)
   *   ROWS    -> @D(Sales Rep Name)
   * )
   *
   * Pass 3 (
   *   COLUMNS -> @D(Q of Order Created Date)
   *   ROWS    -> @D(Sales Rep Name) @D(Web Event Quarter)
   * )
   *
   * Pass 4 (
   *   COLUMNS -> @D(Q of Order Created Date)
   *   ROWS    -> @D(Sales Rep Name)
   * )
   */

  let { column, x } = query;
  while (canSubtotalChannels([column, x])) {
    const { result, dropped: droppedCol } = dropLastTotalableFieldOfChannels([column, x]);
    [column, x] = result;

    // The initial { row, y } of inner [column] subtotal queries
    // needs to be from the base query, not a permuted copy;
    // otherwise we'll miss valid permutations
    let { row, y } = query;

    while (canSubtotalChannels([row, y])) {
      const { result, dropped: droppedRow } = dropLastTotalableFieldOfChannels([row, y]);
      [row, y] = result;

      // Drop marks associated with dropped columns and rows
      const marks = removeMarksMatching(query.marks, new Map([...droppedCol, ...droppedRow]));
      plans.push(
        makeSubtotalPivotBasic(
          model,
          viewName,
          {
            ...query,
            column,
            x,
            row,
            y,
            marks,
          },
          'all',
          series,
          [column, x, row, y]
        )
      );
    }
  }
}

function buildSubtotals(
  model: Model<ModeRole>,
  viewName: string,
  plans: PivotBasic<ModeRole>[],
  query: PivotQuery.Basic<DataRole>,
  channels: ['column', 'x'] | ['row', 'y'],
  key: 'col' | 'row',
  series: string,
  hasGrandTotal: boolean
) {
  const [facetChannel, axisChannel] = channels;
  let facetFields = query[facetChannel];
  let axisFields = query[axisChannel];
  while (canSubtotalChannels([facetFields, axisFields])) {
    const { result, dropped } = dropLastTotalableFieldOfChannels([facetFields, axisFields]);
    [facetFields, axisFields] = result;
    const marks = removeMarksMatching(query.marks, dropped);
    plans.push(
      makeSubtotalPivotBasic(
        model,
        viewName,
        {
          ...query,
          [facetChannel]: facetFields,
          [axisChannel]: axisFields,
          marks,
        },
        key,
        series,
        [facetFields, axisFields]
      )
    );

    // If we also have grand totals, then we need a query for the
    // total of the subtotals
    if (hasGrandTotal) {
      const oppositeFacetChannel = facetChannel === 'column' ? 'row' : 'column';
      const oppositeAxisChannel = axisChannel === 'x' ? 'y' : 'x';

      const [[oppositeFacetFields, oppositeAxisFields], removed] = dropDiscreteOfChannels([
        query[oppositeFacetChannel],
        query[oppositeAxisChannel],
      ]);
      const marks = removeMarksMatching(query.marks, removed);
      plans.push(
        makeSubtotalPivotBasic(
          model,
          viewName,
          {
            ...query,
            [facetChannel]: facetFields,
            [axisChannel]: axisFields,
            [oppositeFacetChannel]: oppositeFacetFields,
            [oppositeAxisChannel]: oppositeAxisFields,
            marks,
          },
          `${key}-gt`,
          series,
          [facetFields, axisFields]
        )
      );
    }
  }
}

function makeSubtotalPivotBasic(
  model: Model<ModeRole>,
  viewName: string,
  query: Partial<PivotQuery.Basic<ModeRole>>,
  key: 'col' | 'row' | 'all' | 'row-gt' | 'col-gt',
  series: string,
  fields: Array<readonly Field<DataRole>[] | undefined>
) {
  query = dropUnusedSorts(query);
  // Naming convention: st:<series>:<dir>:<path>
  const path = fields
    .flat()
    .filter((f): f is Field<DataRole> => f != null)
    .map((f) => f.alias)
    .join(',');
  const name = `st:${series}:${key}:${path}`;
  return PivotBasic.make(model, viewName, PivotQuery.Basic.make({ ...query, name }));
}

function dropUnusedSorts<T extends Partial<PivotQuery.Basic<ModeRole>>>(query: T): T {
  let result = query;

  /**
   * Any fields that were filtered out while creating grand total
   * queries need to be removed from the query sorts. Can't sort
   * by a field that's not in the query
   */
  if (query.sorts) {
    const fields = [
      ...(query.column ?? []),
      ...(query.x ?? []),
      ...(query.row ?? []),
      ...(query.y ?? []),
      ...(query.names ?? []),
      ...(query.values ?? []),
      ...PivotQuery.Marks.fields(query.marks ?? {}),
    ];

    const sorts = query.sorts.filter((sort) =>
      fields.some((field) => {
        return sort.field.alias === field.alias;
      })
    );

    result = {
      ...result,
      sorts,
    };
  }

  return result;
}

function isTotalableField(field: Field<ModeRole>) {
  return field.type === Attr.Type.Discrete && field.source !== Attr.Source.Names;
}

function canSubtotalChannels(channels: (readonly Field<ModeRole>[] | undefined)[]) {
  return channels.reduce((n, next = []) => n + next.filter(isTotalableField).length, 0) > 1;
}

function dropLastTotalableFieldOfChannels(channels: (readonly Field<ModeRole>[] | undefined)[]) {
  const result = channels.slice();
  const dropped: FieldMap = new Map();

  for (let i = result.length; i--; ) {
    const fields = result[i];
    if (fields != null) {
      const { filtered, removed } = removeLastTotalableField(fields);
      if (filtered.length < fields.length) {
        result[i] = filtered;
        if (removed) {
          dropped.set(removed.formula.source, removed);
        }
        break;
      }
    }
  }

  return { result, dropped };
}

function removeLastTotalableField(fields: readonly Field<ModeRole>[]) {
  const filtered = fields.slice();
  let removed: Field<ModeRole> | undefined;
  for (let i = filtered.length; i--; ) {
    const field = filtered[i];
    if (isTotalableField(field)) {
      removed = field;
      filtered.splice(i, 1);
      break;
    }
  }

  return { filtered, removed };
}

function dropDiscreteOfChannels(
  channels: (readonly Field<ModeRole>[] | undefined)[]
): [readonly Field<ModeRole>[][], Readonly<FieldMap>] {
  const removed: FieldMap = new Map();
  const filtered = channels.map((fields = []) => {
    const [continuous, rest] = partition(fields, (f) => f.type === Attr.Type.Continuous);
    // Accumulate all the removed discrete fields by formula
    rest.forEach((f) => {
      removed.set(f.formula.source, f);
    });
    return continuous;
  });
  return [filtered, removed];
}

function removeMarksMatching(marks: PivotQuery.Marks<ModeRole> = {}, map: Readonly<FieldMap>) {
  function matches(f1: Field<ModeRole>, f2: Field<ModeRole> | undefined) {
    return f2 != null && f1.type === f2.type && f1.role === f2.role && f1.source === f2.source;
  }

  return Object.fromEntries(
    Object.entries(marks).map(([channel, fields]) => {
      return [channel, fields.filter((f) => !matches(f, map.get(f.formula.source)))];
    })
  );
}
